<template>
  <div
    class="border-gray relative flex w-full flex-col rounded-lg border bg-white shadow-md"
  >
    <span
      v-if="loadRateText"
      class="absolute -top-2 right-3 rounded px-2 pb-0.5 text-xs"
      :class="loadRateClass"
    >
      {{ loadRateText }}
    </span>

    <router-link
      :to="{
        name: routerLinkName,
        params: { id: load.id },
      }"
      :target="openInNewTab ? '_blank' : null"
      class="p-3"
      @click="handleSelectLoad"
    >
      <div class="mb-1 flex justify-between">
        <div class="flex flex-col items-start pl-3 pt-2">
          <img
            v-if="brokerLogo && showImage"
            class="flex h-4 items-center text-2xs"
            :src="brokerLogo"
            alt="broker logo"
            @error="handleImageError"
          />
          <p
            v-else
            class="font-semibold text-steel-800"
          >
            {{ load.broker_name }}
          </p>
        </div>

        <div class="flex flex-col items-end">
          <p class="text-xl font-bold text-dark-gray">
            {{ formatPrice(load.price) }}
          </p>
          <div class="flex items-center gap-1">
            <p
              v-if="ratePerMile"
              class="text-sm font-normal text-steel-700"
            >
              {{ ratePerMile }}/mi
            </p>
            <span
              v-else
              class="h-5"
            ></span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex gap-2">
          <div class="ml-3 flex flex-col flex-wrap items-start gap-1">
            <p class="mr-3 min-w-fit text-left text-base font-bold capitalize">
              {{ originText }}
            </p>

            <p
              v-if="!isRecommended"
              class="text-left text-xs font-normal text-steel-700"
            >
              {{
                formatStopDate(
                  load?.pickup_time,
                  load?.pickup_time_timezone,
                  load?.pickup_has_time
                )
              }}
            </p>
          </div>
        </div>

        <img
          v-if="isRecommended"
          class="absolute left-2 top-[70px] -ml-0.5 h-10 w-3"
          src="@/assets/loads/line-yellow-small.svg"
          alt="yellow icon"
        />

        <img
          v-else
          class="absolute left-2 top-[70px] -ml-0.5 h-14 w-3"
          src="@/assets/loads/line-yellow.svg"
          alt="yellow icon"
        />

        <div class="flex gap-2">
          <div class="ml-3 flex flex-col flex-wrap items-start gap-1">
            <p class="mr-3 min-w-fit text-left text-base font-bold capitalize">
              {{ destinationText }}
            </p>

            <p
              v-if="!isRecommended"
              class="text-xs font-normal text-steel-700"
            >
              {{
                formatStopDate(
                  load?.dropoff_time,
                  load?.dropoff_time_timezone,
                  load?.dropoff_has_time
                )
              }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="!isRecommended"
        class="mt-4 flex flex-wrap gap-2"
      >
        <span
          v-for="type in equipmentTypes"
          :key="type"
          class="rounded-md bg-gray-100 px-2 py-0.5 text-xs"
        >
          {{ capitalizeWords(type) }}
        </span>
        <span
          v-if="loadWeight"
          class="rounded-md bg-gray-100 px-2 py-0.5 text-xs"
        >
          {{ loadWeight }}
        </span>
      </div>
    </router-link>

    <div
      class="mt-auto flex items-center justify-between rounded-b-lg bg-steel-100 px-3 py-2"
    >
      <div class="flex gap-2">
        <div class="flex items-center gap-1">
          <img
            src="@/assets/loads/arrows-yellow.svg"
            alt="arrows"
          />
          <p class="text-xs font-normal text-steel-800">
            {{ billableMiles }} mi
          </p>
        </div>

        <div class="flex items-center gap-1">
          <img
            src="@/assets/loads/route-yellow.svg"
            alt="route"
          />
          <p class="text-xs font-normal text-steel-800">
            {{ load?.number_of_stops }} stop{{
              load?.number_of_stops > 1 ? 's' : ''
            }}
          </p>
        </div>
      </div>
      <p class="text-xs font-normal text-steel-800">
        {{ fromNowFirstUnit(load.last_posted_at) }} ago
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import { fromNowFirstUnit } from '@/common';
import { formatPrice, formatStopDate, capitalizeWords } from '@/utils';

const store = useStore();

const props = defineProps({
  load: Object,
  isRecommended: {
    type: Boolean,
    default: false,
  },
  openInNewTab: {
    type: Boolean,
    default: false,
  },
});

const firstStop = computed(() => props.load?.stops?.[0]);

const originText = computed(() => {
  const city = firstStop?.value?.city || firstStop?.value?.area_five_zip_city;
  const state =
    firstStop?.value?.state || firstStop?.value?.area_five_zip_state;

  return `${city?.toLowerCase()}${state ? ', ' + state : ''}`;
});

const destinationText = computed(() => {
  const city = lastStop?.value?.city || lastStop?.value?.area_five_zip_city;
  const state = lastStop?.value?.state || lastStop?.value?.area_five_zip_state;

  return `${city?.toLowerCase()}${state ? ', ' + state : ''}`;
});

const lastStop = computed(
  () => props.load?.stops?.[props.load?.stops?.length - 1]
);

const routerLinkName = computed(() => 'Load Detail');

const billableMiles = computed(() => {
  const distance = props?.load?.posted_miles;

  return Math.round(distance * 100) / 100;
});

const equipmentTypes = computed(
  () => props.load?.posted_equipment_types?.slice(0, 2) || []
);

const weightUnit = {
  pound: 'lbs',
  kilogram: 'kg',
};

const loadWeight = computed(() => {
  if (!props.load?.weight) {
    return null;
  }

  return `${props.load?.weight?.toLocaleString('en-US')} ${weightUnit[props.load?.weight_unit]}`;
});

const brokerLogo = computed(() => props?.load?.broker_logo?.url);

const loadRate = computed(() => {
  return props?.load?.lane_prediction?.posted_rate_assessment;
});

const loadRateClass = computed(() => {
  switch (loadRate?.value) {
    case 'Great':
      return 'bg-aqua-700 text-white';
    case 'Good':
      return 'bg-aqua-500 text-white';
    case 'Fair':
      return 'bg-aqua-300 text-steel-1100';
    case 'Below':
      return 'bg-red-100 text-red-600';
    case 'Backhaul':
      return 'bg-sun-400 text-steel-1100';
    default:
      return '';
  }
});

const loadRateText = computed(() => {
  switch (loadRate?.value) {
    case 'Great':
      return 'Great Deal';
    case 'Good':
      return 'Good Deal';
    case 'Backhaul':
      return 'Backhaul';
    default:
      return null;
  }
});

const showImage = ref(true);
const handleImageError = () => {
  if (showImage?.value) {
    showImage.value = false;
  }
};

const ratePerMile = computed(() => {
  if (!props?.load?.lane_prediction && !props?.load?.rate_per_mile) return null;

  return formatPrice(props?.load?.rate_per_mile);
});

const handleSelectLoad = async () => {
  if (!props?.openInNewTab) {
    store.commit('loads/setPreLoadResponse', props?.load);
    store.commit('loads/setLoadDetail', {
      ...props?.load,
      stops: props?.load?.stops?.map((stop) => ({
        ...stop,
        latitude_for_map: stop?.lat,
        longitude_for_map: stop?.lon,
      })),
    });
  }
  await store.dispatch('activities/postActivity', {
    user_activity: {
      is_type: 'recommended_load_click',
      action_id: props?.load?.id,
    },
  });
};
</script>
